/**
 * AdminFinanceDocumentsPage — Belege & Mietzahlungen.
 *
 * SP3 (Phase 1b Stop-Punkt 3): page state, filters, list rendering.
 * Modal (SP4), status-pill toggle (SP5) and soft-delete dialog (SP6)
 * come in subsequent stop-points. The "+ Beleg erfassen" button, the
 * status pill and the row-click are wired as visual-only / disabled
 * placeholders in this commit.
 *
 * Naming-conflict mitigation per docs/spec-finance-frontend.md §5.3:
 * the subtitle cross-references AdminExpensesPage (Soll-Werte aus
 * unit_costs). This page is the Ist-Beleg-Store from finance_documents.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  fetchAdminFinanceDocuments,
  fetchAdminFinanceDocumentFileUrl,
  patchAdminFinanceDocumentStatus,
} from "../../api/adminData";
import FinanceDocumentModal from "../../components/admin/FinanceDocumentModal";
import { formatDateOnly } from "../../utils/datetime";


// ─── Constants ──────────────────────────────────────────────────────


/**
 * Authoritative whitelist — must match backend CATEGORIES_BY_TYPE in
 * app/services/finance_document_service.py.
 */
const CATEGORIES_BY_TYPE = {
  expense: [
    { value: "miete_vermieter", label: "Miete Vermieter" },
    { value: "moebel_einrichtung", label: "Möbel & Einrichtung" },
    { value: "reinigung", label: "Reinigung" },
    { value: "reparatur_wartung", label: "Reparatur & Wartung" },
    { value: "verbrauchsmaterial", label: "Verbrauchsmaterial" },
    { value: "internet_software", label: "Internet & Software" },
    { value: "marketing", label: "Marketing" },
    { value: "versicherung", label: "Versicherung" },
    { value: "buchhaltung_beratung", label: "Buchhaltung & Beratung" },
    { value: "gebuehren_banken", label: "Gebühren & Banken" },
    { value: "sonstiges", label: "Sonstiges" },
  ],
  income: [
    { value: "kaution", label: "Kaution" },
    { value: "reinigungspauschale", label: "Reinigungspauschale" },
    { value: "schadenersatz", label: "Schadenersatz" },
    { value: "rueckerstattung", label: "Rückerstattung" },
    { value: "sonstiges", label: "Sonstiges" },
  ],
  rent_payment: [
    { value: "mieteingang", label: "Mieteingang" },
  ],
};

/** Flat list for the filter-bar category dropdown. "sonstiges" once. */
const ALL_CATEGORIES_FOR_FILTER = [
  ...CATEGORIES_BY_TYPE.expense,
  ...CATEGORIES_BY_TYPE.income.filter((c) => c.value !== "sonstiges"),
  ...CATEGORIES_BY_TYPE.rent_payment,
];

const CATEGORY_LABEL_BY_VALUE = Object.fromEntries(
  ALL_CATEGORIES_FOR_FILTER.map((c) => [c.value, c.label])
);

const TYPE_LABEL = {
  expense: "Ausgabe",
  income: "Einnahme",
  rent_payment: "Mieteingang",
};


// ─── Format helpers ─────────────────────────────────────────────────


function formatCHF(amount) {
  if (amount == null || isNaN(Number(amount))) return "—";
  const n = Number(amount);
  // de-CH locale formats with apostrophe as thousands separator.
  return `CHF ${n.toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function categoryLabel(value) {
  return CATEGORY_LABEL_BY_VALUE[value] || value || "—";
}


// ─── Sub-components ─────────────────────────────────────────────────


function TypePill({ value }) {
  const label = TYPE_LABEL[value] || value;
  const colorClass =
    value === "expense"
      ? "bg-rose-500/15 text-rose-300 border-rose-500/30"
      : value === "income"
      ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
      : "bg-sky-500/15 text-sky-300 border-sky-500/30"; // rent_payment
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.05em] ${colorClass}`}
    >
      {label}
    </span>
  );
}

/**
 * Status pill: amber for "new", green for "bexio_recorded".
 * SP5: interactive toggle with per-row lock during the in-flight
 * PATCH /status request. Optimistic update + rollback-on-error
 * pattern lives in the parent's handleToggleStatus (spec §8.2).
 *
 * Multi-tab race (R2) is accepted in v1 — see spec §8.4.
 */
function StatusPill({ doc, onToggle, isBusy }) {
  const isNew = doc.status === "new";
  const baseClass =
    "inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.05em] transition-opacity";
  const stateClass = isNew
    ? "bg-amber-500/15 text-amber-300 border border-amber-500/30"
    : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30";
  const interactionClass = isBusy
    ? "opacity-60 cursor-not-allowed"
    : "cursor-pointer hover:brightness-110";
  return (
    <button
      type="button"
      disabled={isBusy}
      onClick={(e) => {
        // stopPropagation prevents the row-level onClick (which opens
        // the edit modal) from firing when the pill is clicked.
        e.stopPropagation();
        onToggle(doc);
      }}
      className={`${baseClass} ${stateClass} ${interactionClass}`}
      title={isNew ? "Auf 'In Bexio' setzen" : "Auf 'Neu' zurücksetzen"}
    >
      {isNew ? "Neu" : "✓ In Bexio"}
    </button>
  );
}

function TypeFilterButtons({ value, onChange }) {
  const options = [
    { v: "", label: "Alle" },
    { v: "expense", label: "Ausgaben" },
    { v: "income", label: "Einnahmen" },
    { v: "rent_payment", label: "Mieteingänge" },
  ];
  return (
    <div>
      <label className="mb-1 block text-[10px] font-medium uppercase tracking-[0.09em] text-[#4a5070]">
        Typ
      </label>
      <div className="inline-flex rounded-[8px] border border-[#1e2130] bg-[#0d0f14] p-[3px]">
        {options.map((o) => {
          const isActive = value === o.v;
          return (
            <button
              key={o.v || "all"}
              type="button"
              onClick={() => onChange(o.v)}
              className={
                isActive
                  ? "rounded-[6px] bg-[#5b8cff] px-3 py-1.5 text-[12px] font-semibold text-white"
                  : "rounded-[6px] bg-transparent px-3 py-1.5 text-[12px] font-medium text-[#94a3b8] hover:text-[#c5cbe0]"
              }
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}


// ─── Main page ──────────────────────────────────────────────────────


export default function AdminFinanceDocumentsPage() {
  // Liste + Loading + Error
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filter — sofort-triggernd
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Filter — debounced (search-Input only)
  const [searchInput, setSearchInput] = useState("");
  const [searchApplied, setSearchApplied] = useState("");

  // Pro-Row Status-Toggle in-flight Lock (Set<string>).
  // SP5: active — set during PATCH /status, released in finally.
  const [statusBusy, setStatusBusy] = useState(new Set());

  // Pro-Row File-Download in-flight Lock (Set<string>).
  // SP4.5: signed-URL request typically <500ms; lock prevents
  // spam-clicks from issuing redundant signing requests.
  const [fileBusy, setFileBusy] = useState(new Set());

  // Modal-State (SP4)
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [modalInitialValues, setModalInitialValues] = useState(null);

  function handleOpenCreate() {
    setModalMode("create");
    setModalInitialValues(null);
    setModalOpen(true);
  }

  function handleOpenEdit(doc) {
    setModalMode("edit");
    setModalInitialValues(doc);
    setModalOpen(true);
  }

  function handleCloseModal() {
    setModalOpen(false);
  }

  /**
   * Status-toggle handler (spec §8.2 — 5-stage pattern).
   *
   *   (0) Re-entry guard: already in-flight for this row?
   *   (1) Optimistic update — patch the list immediately
   *   (2) Lock the pill (disabled + opacity-60 + cursor-not-allowed)
   *   (3) API call; on success the server response overwrites the
   *       optimistic state
   *   (4) On error: rollback to previousStatus + toast.error
   *   (5) Release the lock in finally — runs on success AND error
   */
  async function handleToggleStatus(doc) {
    if (statusBusy.has(doc.id)) return;

    const previousStatus = doc.status;
    const newStatus = previousStatus === "new" ? "bexio_recorded" : "new";

    // (1) Optimistic update
    setItems((prev) =>
      prev.map((d) => (d.id === doc.id ? { ...d, status: newStatus } : d))
    );

    // (2) Lock
    setStatusBusy((prev) => {
      const next = new Set(prev);
      next.add(doc.id);
      return next;
    });

    try {
      const updated = await patchAdminFinanceDocumentStatus(doc.id, newStatus);
      // (3) Server response is source of truth
      setItems((prev) =>
        prev.map((d) => (d.id === doc.id ? updated : d))
      );
    } catch (err) {
      // (4) Rollback + toast
      setItems((prev) =>
        prev.map((d) =>
          d.id === doc.id ? { ...d, status: previousStatus } : d
        )
      );
      toast.error(
        err?.message ||
          "Status konnte nicht geändert werden — bitte erneut versuchen."
      );
    } finally {
      // (5) Release lock — egal ob success oder error
      setStatusBusy((prev) => {
        const next = new Set(prev);
        next.delete(doc.id);
        return next;
      });
    }
  }

  /**
   * Download handler — fetches the signed R2 URL and opens it in a
   * new tab. Per-row lock via fileBusy prevents accidental
   * double-clicks while the URL is being signed (~500ms typical).
   * `noopener,noreferrer` on window.open prevents the new tab from
   * accessing this page via window.opener (security).
   */
  async function handleDownload(doc) {
    if (fileBusy.has(doc.id)) return;
    if (!doc.has_file) {
      toast.error("Kein Datei-Anhang vorhanden.");
      return;
    }

    setFileBusy((prev) => {
      const next = new Set(prev);
      next.add(doc.id);
      return next;
    });

    try {
      const result = await fetchAdminFinanceDocumentFileUrl(doc.id);
      if (result?.url) {
        window.open(result.url, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Datei-URL konnte nicht erzeugt werden.");
      }
    } catch (err) {
      toast.error(err?.message || "Download fehlgeschlagen.");
    } finally {
      setFileBusy((prev) => {
        const next = new Set(prev);
        next.delete(doc.id);
        return next;
      });
    }
  }

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    return fetchAdminFinanceDocuments({
      type: typeFilter || undefined,
      status: statusFilter || undefined,
      category: categoryFilter || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      q: searchApplied || undefined,
      limit: 200,
    })
      .then((data) => {
        setItems(Array.isArray(data?.items) ? data.items : []);
      })
      .catch((e) => {
        setError(e?.message || "Belege konnten nicht geladen werden.");
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [typeFilter, statusFilter, categoryFilter, dateFrom, dateTo, searchApplied]);

  // (a) Sofort-triggernd: alle Filter außer search-Input
  useEffect(() => {
    void load();
  }, [load]);

  // (b) Debounce: 300ms nach letztem Tippen → searchApplied = searchInput
  useEffect(() => {
    if (searchInput === searchApplied) return undefined;
    const timer = setTimeout(() => {
      setSearchApplied(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchApplied]);

  // Auto-reset categoryFilter if it becomes invalid after a type change
  // (e.g. type=expense + category=mieteingang). Backend would just return
  // 0 rows but the UX is clearer if we drop the stale selection.
  useEffect(() => {
    if (!typeFilter || !categoryFilter) return;
    const valid = CATEGORIES_BY_TYPE[typeFilter].some(
      (c) => c.value === categoryFilter
    );
    if (!valid) setCategoryFilter("");
  }, [typeFilter, categoryFilter]);

  const hasFilters = useMemo(
    () =>
      Boolean(
        typeFilter ||
          statusFilter ||
          categoryFilter ||
          dateFrom ||
          dateTo ||
          searchInput
      ),
    [typeFilter, statusFilter, categoryFilter, dateFrom, dateTo, searchInput]
  );

  function handleResetFilters() {
    setTypeFilter("");
    setStatusFilter("");
    setCategoryFilter("");
    setDateFrom("");
    setDateTo("");
    setSearchInput("");
    setSearchApplied("");
  }

  // Dynamically filter category dropdown by selected type.
  const categoryOptionsForFilter = useMemo(() => {
    if (!typeFilter) return ALL_CATEGORIES_FOR_FILTER;
    return CATEGORIES_BY_TYPE[typeFilter];
  }, [typeFilter]);

  const empty = !loading && items.length === 0 && !error;

  return (
    <div className="mx-auto max-w-[1400px] p-4 md:p-6">
      {/* ─── §5.1 Header ───────────────────────────────────────── */}
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="m-0 text-[22px] font-bold tracking-tight text-[#e2e4ed]">
            📥 Belege
          </h1>
          <p className="mt-1 text-[12px] text-[#4a5070]">
            Sammelstelle für Belege & Mietzahlungen (Quittungen,
            Eingangsrechnungen, Zahlungseingänge). Für die monatlichen
            Sollwerte siehe{" "}
            <Link
              to="/admin/ausgaben"
              className="text-[#5b8cff] underline decoration-dotted hover:text-[#7aa3ff]"
            >
              💸 Ausgaben
            </Link>
            .
          </p>
        </div>
        <button
          type="button"
          onClick={handleOpenCreate}
          className="rounded-[10px] bg-[#5b8cff] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#4a7ae8]"
        >
          + Beleg erfassen
        </button>
      </header>

      {/* ─── §5.2 Filter-Bar ───────────────────────────────────── */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <TypeFilterButtons value={typeFilter} onChange={setTypeFilter} />

        <div>
          <label className="mb-1 block text-[10px] font-medium uppercase tracking-[0.09em] text-[#4a5070]">
            Status
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="block w-full sm:w-[180px] rounded-[8px] border border-[#1e2130] bg-[#0d0f14] px-3 py-2 text-[13px] text-[#c5cbe0] outline-none focus:border-[#3a3f55]"
          >
            <option value="">Alle Status</option>
            <option value="new">Neu</option>
            <option value="bexio_recorded">In Bexio</option>
          </select>
        </div>

        <div>
          <label className="mb-1 block text-[10px] font-medium uppercase tracking-[0.09em] text-[#4a5070]">
            Kategorie
          </label>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="block w-full sm:w-[220px] rounded-[8px] border border-[#1e2130] bg-[#0d0f14] px-3 py-2 text-[13px] text-[#c5cbe0] outline-none focus:border-[#3a3f55]"
          >
            <option value="">Alle Kategorien</option>
            {categoryOptionsForFilter.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-[10px] font-medium uppercase tracking-[0.09em] text-[#4a5070]">
            Datum von
          </label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="block w-full sm:w-[150px] rounded-[8px] border border-[#1e2130] bg-[#0d0f14] px-3 py-2 text-[13px] text-[#c5cbe0] outline-none focus:border-[#3a3f55]"
          />
        </div>

        <div>
          <label className="mb-1 block text-[10px] font-medium uppercase tracking-[0.09em] text-[#4a5070]">
            Datum bis
          </label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="block w-full sm:w-[150px] rounded-[8px] border border-[#1e2130] bg-[#0d0f14] px-3 py-2 text-[13px] text-[#c5cbe0] outline-none focus:border-[#3a3f55]"
          />
        </div>

        <div className="flex-1 min-w-[180px]">
          <label className="mb-1 block text-[10px] font-medium uppercase tracking-[0.09em] text-[#4a5070]">
            Suche
          </label>
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Beschreibung suchen …"
            className="block w-full sm:w-[220px] rounded-[8px] border border-[#1e2130] bg-[#0d0f14] px-3 py-2 text-[13px] text-[#c5cbe0] outline-none focus:border-[#3a3f55]"
          />
        </div>

        {hasFilters ? (
          <button
            type="button"
            onClick={handleResetFilters}
            className="rounded-[8px] border border-[#1e2130] bg-transparent px-3 py-2 text-[12px] font-medium text-[#94a3b8] hover:bg-[#13161f]"
          >
            Filter zurücksetzen
          </button>
        ) : null}
      </div>

      {/* ─── §5.6 Error + Loading ──────────────────────────────── */}
      {error ? <p className="mb-4 text-sm text-[#ff6b6b]">{error}</p> : null}
      {loading ? (
        <p className="text-[13px] text-[#4a5070]">Lade Belege …</p>
      ) : null}

      {/* ─── §5.6 Empty-State ──────────────────────────────────── */}
      {empty ? (
        <div className="rounded-[10px] border border-dashed border-[#1e2130] bg-[#0d0f14] p-8 text-center">
          <p className="mb-3 text-[13px] font-medium text-[#94a3b8]">
            {hasFilters
              ? "Keine Belege passen zu den Filtern."
              : "Noch keine Belege erfasst. Ersten Beleg erfassen?"}
          </p>
          <button
            type="button"
            onClick={handleOpenCreate}
            className="rounded-[10px] bg-[#5b8cff] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#4a7ae8]"
          >
            + Beleg erfassen
          </button>
        </div>
      ) : null}

      {/* ─── §5.5 Mobile-Card-Stack (< md) ─────────────────────── */}
      {!loading && items.length > 0 ? (
        <div className="block md:hidden space-y-2">
          {items.map((doc) => (
            <article
              key={doc.id}
              onClick={(e) => {
                if (e.target.closest("button, a, input, select")) return;
                handleOpenEdit(doc);
              }}
              className="cursor-pointer rounded-[10px] border border-[#1e2130] bg-[#0d0f14] p-4 hover:bg-[#13161f]"
            >
              {/* Row 1: Datum + Status-Pill */}
              <div className="flex items-start justify-between gap-2">
                <div className="text-[14px] font-semibold text-[#c5cbe0] tabular-nums">
                  {formatDateOnly(doc.document_date)}
                </div>
                <StatusPill
                  doc={doc}
                  onToggle={handleToggleStatus}
                  isBusy={statusBusy.has(doc.id)}
                />
              </div>

              {/* Row 2: Typ-Pill + Kategorie */}
              <div className="mt-2 flex items-center gap-2">
                <TypePill value={doc.type} />
                <span className="text-[12px] text-[#94a3b8]">
                  {categoryLabel(doc.category)}
                </span>
              </div>

              {/* Row 3: Betrag (groß rechts) */}
              <div className="mt-3 text-right text-[20px] font-bold tabular-nums text-[#c5cbe0]">
                {formatCHF(doc.amount_chf)}
              </div>

              {/* Row 4: Beschreibung (max 2 Zeilen) */}
              {doc.description ? (
                <p className="mt-2 line-clamp-2 text-[12px] text-[#94a3b8]">
                  {doc.description}
                </p>
              ) : null}

              {/* Row 5: Footer (Bezahlt von + Datei-Indikator) */}
              <div className="mt-3 flex items-center justify-between gap-2 text-[11px]">
                <span className="text-[#94a3b8]">
                  {doc.paid_by_user_label || "—"}
                </span>
                {doc.has_file ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(doc);
                    }}
                    disabled={fileBusy.has(doc.id)}
                    className={`text-[11px] ${
                      fileBusy.has(doc.id)
                        ? "text-[#4a5070] cursor-not-allowed opacity-60"
                        : "text-[#5b8cff] hover:text-[#7aa3ff]"
                    }`}
                  >
                    📥 Datei öffnen
                  </button>
                ) : (
                  <span className="text-[#4a5070]"></span>
                )}
              </div>
            </article>
          ))}
        </div>
      ) : null}

      {/* ─── §5.4 Desktop-Tabelle (≥ md) ───────────────────────── */}
      {!loading && items.length > 0 ? (
        <div className="hidden md:block overflow-x-auto rounded-[10px] border border-[#1e2130] bg-[#13161f]">
          <table className="w-full text-left text-[13px]">
            <thead>
              <tr className="border-b border-[#1e2130] text-[10px] font-bold uppercase tracking-[0.09em] text-[#4a5070]">
                <th className="px-4 py-3 whitespace-nowrap">Datum</th>
                <th className="px-4 py-3 whitespace-nowrap">Typ</th>
                <th className="px-4 py-3 whitespace-nowrap">Kategorie</th>
                <th className="px-4 py-3 whitespace-nowrap text-right">Betrag</th>
                <th className="px-4 py-3 whitespace-nowrap">Bezahlt von</th>
                <th className="px-4 py-3 whitespace-nowrap">Beschreibung</th>
                <th className="px-4 py-3 whitespace-nowrap">Datei</th>
                <th className="px-4 py-3 whitespace-nowrap">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((doc) => (
                <tr
                  key={doc.id}
                  onClick={(e) => {
                    if (e.target.closest("button, a, input, select")) return;
                    handleOpenEdit(doc);
                  }}
                  className="cursor-pointer border-b border-[#1e2130]/60 hover:bg-[#1a1e2a]"
                >
                  <td className="px-4 py-3 tabular-nums text-[#c5cbe0]">
                    {formatDateOnly(doc.document_date)}
                  </td>
                  <td className="px-4 py-3">
                    <TypePill value={doc.type} />
                  </td>
                  <td className="px-4 py-3 text-[#94a3b8]">
                    {categoryLabel(doc.category)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-[#c5cbe0]">
                    {formatCHF(doc.amount_chf)}
                  </td>
                  <td className="px-4 py-3 text-[#94a3b8]">
                    {doc.paid_by_user_label || "—"}
                  </td>
                  <td className="px-4 py-3 text-[#94a3b8] max-w-[260px] truncate">
                    {doc.description || "—"}
                  </td>
                  <td className="px-4 py-3">
                    {doc.has_file ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(doc);
                        }}
                        disabled={fileBusy.has(doc.id)}
                        title="Beleg-Datei herunterladen"
                        className={`text-[12px] ${
                          fileBusy.has(doc.id)
                            ? "text-[#4a5070] cursor-not-allowed opacity-60"
                            : "text-[#5b8cff] hover:text-[#7aa3ff] cursor-pointer"
                        }`}
                      >
                        📥 Öffnen
                      </button>
                    ) : (
                      <span className="text-[#4a5070]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill
                  doc={doc}
                  onToggle={handleToggleStatus}
                  isBusy={statusBusy.has(doc.id)}
                />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* ─── SP4 — Create / Edit Modal ─────────────────────────── */}
      <FinanceDocumentModal
        open={modalOpen}
        mode={modalMode}
        initialValues={modalInitialValues}
        onClose={handleCloseModal}
        onSubmitted={() => {
          handleCloseModal();
          void load();
        }}
      />
    </div>
  );
}
